import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Shield, Wallet, PieChart, Zap, Sparkles, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Shield,
      title: "Non-Custodial Security",
      description: "Your keys, your coins. Ed25519 cryptography secures your wallets — Xavier never holds your keys."
    },
    {
      icon: Wallet,
      title: "12-Chain Support",
      description: "Manage BTC, ETH, SOL, XRP, and 8 more chains all in one unified interface"
    },
    {
      icon: PieChart,
      title: "4-AI Consensus",
      description: "Four independent AI models deliberate on every recommendation before it reaches you"
    },
    {
      icon: Zap,
      title: "Instant Transactions",
      description: "Send and receive across chains with cryptographically verified signatures"
    }
  ];

  const chains = [
    { name: "XRP", color: "#23292F" },
    { name: "SOL", color: "#9945FF" },
    { name: "XLM", color: "#14B8E0" },
    { name: "HBAR", color: "#3A3A3A" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#000A1F] to-[#001133] overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FFD700]/10 rounded-full blur-[128px]" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#00FFFF]/10 rounded-full blur-[128px]" />
        </div>

        {/* Nav */}
        <nav className="absolute top-0 left-0 right-0 flex items-center justify-between p-6 lg:px-12">
          <div className="flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-[#FFD700]" />
            <span className="text-xl font-bold text-white font-['Syne']"><span className="text-[#FFD700]">X</span>avier</span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              className="text-white/70 hover:text-white"
              onClick={() => navigate('/login')}
              data-testid="nav-login-btn"
            >
              Login
            </Button>
            <Button
              className="btn-gold rounded-none"
              onClick={() => navigate('/register')}
              data-testid="nav-register-btn"
            >
              Get Started
            </Button>
          </div>
        </nav>

        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center max-w-5xl mx-auto"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FFD700]/30 bg-[#FFD700]/5"
          >
            <div className="w-2 h-2 rounded-full bg-[#00FFFF] animate-pulse" />
            <span className="text-[#FFD700] text-sm font-medium tracking-wider uppercase">
              Powered by 4-AI Consensus
            </span>
          </motion.div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 font-['Syne'] tracking-tight">
            <span className="text-[#FFD700]">X</span>avier
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#FDB931] to-[#FFD700]">
              AI Financial Partner
            </span>
          </h1>

          <p className="text-white/60 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Your non-custodial AI financial partner. 
            Manage 12 chains, optimize your portfolio, and grow with confidence.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              className="btn-gold rounded-none text-lg px-12 py-6 group"
              onClick={() => navigate('/register')}
              data-testid="hero-get-started-btn"
            >
              Start Securing
              <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              variant="outline"
              className="btn-outline-gold rounded-none text-lg px-12 py-6"
              onClick={() => navigate('/login')}
              data-testid="hero-login-btn"
            >
              View Dashboard
            </Button>
          </div>

          {/* Chain Badges */}
          <div className="mt-16 flex items-center justify-center gap-6 flex-wrap">
            {chains.map((chain, i) => (
              <motion.div
                key={chain.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="px-6 py-3 rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm"
              >
                <span className="text-white font-semibold">{chain.name}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white font-['Syne'] mb-4">
              Why <span className="text-[#FFD700]">Xavier</span>?
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Built for the next generation of crypto investors who demand intelligence and transparency.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 rounded-2xl group hover:border-[#FFD700]/30 transition-all duration-300"
              >
                <div className="flex items-start gap-6">
                  <div className="p-4 rounded-xl bg-[#FFD700]/10 group-hover:bg-[#FFD700]/20 transition-colors">
                    <feature.icon className="w-8 h-8 text-[#FFD700]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2 font-['Syne']">
                      {feature.title}
                    </h3>
                    <p className="text-white/50 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-12 rounded-3xl relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/5 via-transparent to-[#00FFFF]/5" />
            <div className="relative z-10">
              <Sparkles className="w-16 h-16 text-[#FFD700] mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white font-['Syne'] mb-4">
                Ready to Grow With Confidence?
              </h2>
              <p className="text-white/50 text-lg mb-8 max-w-xl mx-auto">
                Join investors who trust Xavier for intelligent, non-custodial multi-chain portfolio management.
              </p>
              <Button
                className="btn-gold rounded-none text-lg px-12 py-6"
                onClick={() => navigate('/register')}
                data-testid="cta-get-started-btn"
              >
                Create Free Account
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/30 text-sm">
            © 2026 Xavier by NorthStar Technology. Non-custodial. AI-powered. Yours.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
